/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect, useRef } from 'react';
import AvatarEditor from 'react-avatar-editor';
import PropTypes from 'prop-types';
import ReactBootstrapSlider from 'react-bootstrap-slider';
import Modal from '../../../Modal/ReactBootstrapModal';

import styles from './photoEditor.scss';
import { transparentPng } from '../../../../utils/constants';

const SponsorPhotoEditor = ({
    type,
    radius,
    showEditor,
    closeEditor,
    imageUrl,
    uploadCropData,
}) => {
    const [width, setWidth] = useState(0);
    const [scale, setScale] = useState(1.2);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [loadError, setLoadError] = useState(null);

    const editorRef = useRef(null);
    const rootRef = useRef(null);

    const updateDimensions = () => {
        if (rootRef.current && rootRef.current.offsetWidth !== width) {
            setWidth(rootRef.current.offsetWidth);
        }
    };

    useEffect(() => {
        updateDimensions();
        window.addEventListener('resize', updateDimensions);

        return () => {
            window.removeEventListener('resize', updateDimensions);
        };
    }, [width]);

    useEffect(() => {
        if (imageUrl === transparentPng) {
            setImageLoaded(false);
            setSubmitting(false);
        }
        updateDimensions();
    }, [imageUrl]);

    const handleCloseEditor = () => {
        if (!submitting) {
            closeEditor();
            setScale(1.2);
            setImageLoaded(false);
            setSubmitting(false);
        }
    };

    const handleCropImage = async () => {
        if (editorRef.current) {
            setSubmitting(true);
            const rect = editorRef.current.getCroppingRect();
            await uploadCropData({
                cropRectangle: rect,
                tempPhotoUri: imageUrl,
            });
            setSubmitting(false);
            handleCloseEditor();
        }
    };

    const handleImageReady = () => {
        if (editorRef.current.props.image !== transparentPng) {
            setImageLoaded(true);
        }
    };

    const handleLoadFailure = () => {
        setLoadError('Error loading image');
    };

    const getBorder = () => 0.05 * width;

    let modalHeaderTitle = 'Edit Banner Photo';
    if (type === 'event_sponsor_logo') {
        modalHeaderTitle = 'Edit Event Sponsor Logo';
    }

    return (
        <>
            <div className={styles.modal}>
                <div className="modal-header">
                    <div className="text-medium">{modalHeaderTitle}</div>
                    <div className="close-icon" onClick={handleCloseEditor} data-testid="event-edit.sponsor-photo.close-btn">
                        <i className="utr utr-close" />
                    </div>
                </div>
                <div className="modal-body">
                    <div>
                        <div
                            className="text-center"
                            ref={rootRef}
                            style={{ minHeight: '160px' }}
                        >
                            <AvatarEditor
                                ref={editorRef}
                                image={imageUrl}
                                width={712}
                                height={346}
                                border={getBorder()}
                                color={[255, 255, 255, 0.6]} // RGBA
                                scale={scale}
                                rotate={0}
                                borderRadius={radius}
                                onImageReady={handleImageReady}
                                onLoadFailure={handleLoadFailure}
                                style={
                                    !imageLoaded
                                        ? { transition: 'height 0.5s' }
                                        : {}
                                }
                                data-backdrop={false}
                            />
                            {(!imageUrl || !imageLoaded) && showEditor && (
                                <div className={styles.loaderWrapper}>
                                    <div className={styles.loader} />
                                </div>
                            )}
                        </div>
                        <div className={styles.editContainer}>
                            <div className="slider-small slider-gray">
                                <div className="wrapper">
                                    <ReactBootstrapSlider
                                        value={scale}
                                        max={2}
                                        min={1}
                                        step={0.01}
                                        change={e => setScale(e.target.value)}
                                        disabled={
                                            !imageLoaded || submitting
                                                ? 'disabled'
                                                : ''
                                        }
                                    />
                                </div>
                            </div>
                            <button
                                type="button"
                                className="btn btn-primary text-uppercase"
                                onClick={handleCropImage}
                                disabled={!imageLoaded || submitting}
                                data-testid="event-edit.sponsor-photo.save-btn"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <Modal
                backdropClassName={styles.backdrop}
                backdrop={false}
                visible={!!loadError}
                dialogClassName="modal-lg"
            >
                <div className="modal-header">
                    <div className="text-medium">Image Load Error</div>
                    <div
                        className="close-icon"
                        onClick={() => setLoadError(null)}
                        data-testid="event-edit.sponsor-photo.error-close-btn"
                    >
                        <i className="utr utr-close" />
                    </div>
                </div>
                <div className="modal-body">
                    <div>
                        <div className="text-center">
                            <p>{loadError}</p>
                        </div>
                        <div>
                            <button
                                type="button"
                                className="btn btn-primary text-uppercase float-right"
                                onClick={() => setLoadError(null)}
                                data-testid="event-edit.sponsor-photo.error-ok-btn"
                            >
                                Ok
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

SponsorPhotoEditor.propTypes = {
    type: PropTypes.oneOf(['event_sponsor_logo']),
    radius: PropTypes.number,
    showEditor: PropTypes.bool.isRequired,
    closeEditor: PropTypes.func.isRequired,
    imageUrl: PropTypes.string.isRequired,
    uploadCropData: PropTypes.func.isRequired,
};

SponsorPhotoEditor.defaultProps = {
    radius: 0,
    type: {},
};

export default SponsorPhotoEditor;
